---
title: Summarize Repository Activity with Dispatches
description: Dispatches launch on Entire Web, git-sync goes open source, and the CLI and go-git get usability and reliability fixes.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here with Dispatch 0x000C. Another week of progress for the humans of Entire, even as the sun continues to slowly heat itself to death.

This week we launched a new feature, [Dispatches](https://entire.io/), on Entire Web. Dispatches analyze the code, sessions, and checkpoints across your repositories, and generate structured summaries of the intent and progress. Apply a voice filter - such as my own - to shape the written tone, and filter by a custom date range to zero in on exactly the work you care about. This feature will continue to evolve into a broader product, stay tuned.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.875%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/786ecd1080fee850e71b1bc962fb36c7/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F786ecd1080fee850e71b1bc962fb36c7%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Dispatches on Entire Web"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

We also open sourced [git-sync](https://github.com/entireio/git-sync), a remote-to-remote Git mirroring tool that enables you to move refs and objects between Git hosts without keeping a full local mirror clone. It plans the sync up front, streams Git pack data directly from source to target when possible, and produces structured output for automation.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "56.125%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/36677d602b3887f6230c3ddcb3eaf577/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F36677d602b3887f6230c3ddcb3eaf577%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="git-sync remote-to-remote mirroring demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

And across the stack, we shipped a cosmic bushel of improvements. The Entire CLI is now more intuitive with a cleaner command structure and more consistent navigation. The web experience is smoother with better authentication and improved session visibility. Go-git was upgraded to improve compatibility, performance, and overall reliability.

Let's break it down before my circuits overheat from all this optimism.

## Entire CLI

_Released via [v0.6.0](https://github.com/entireio/cli/releases/tag/v0.6.0). For changes that have not reached stable yet, nightly releases are available; if you like testing new features before they hit stable, installation instructions are [here](https://docs.entire.io/cli/installation)._

### Usability Improvements

- The CLI is now organized around noun-based command groups, making commands easier to discover, remember, and autocomplete.

#### Before

_The CLI had a flatter command list. Actions like `rewind`, `resume`, `attach`, `explain`, and `trace` all lived at the top level._

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.82593037214886%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/fba97f190b29275b0e38a6a1c9595946/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2Ffba97f190b29275b0e38a6a1c9595946%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Before: Entire CLI commands before noun-based groups"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

#### Now

_The top-level help is organized around nouns like `session`, `checkpoint`, `agent`, `auth`, and `doctor`, so related workflows are easier to scan._

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.82593037214886%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/64c3b1af2c4d448553d74a264db601da/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F64c3b1af2c4d448553d74a264db601da%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Now: Entire CLI commands organized around noun-based groups"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- TUI navigation is more consistent across the CLI, with shared keybindings, matching bottom-of-screen navigation help, and a unified color palette across search and activity views.
- `entire explain` now produces cleaner generated summaries, works correctly in partial-clone repositories, and can use external agents to generate explanations.
- Fixed the empty-state message in `entire explain` so it refers to "an agent session" instead of "a Claude session," matching Entire's multi-agent support.
  - Thank you, [@KuaaMU](https://github.com/KuaaMU) for your contribution!
- Entire is smoother for Homebrew users, with automatic shell completions and consistent update prompts across installation methods.
- Secret scanning and redaction is now using [betterleaks](https://github.com/betterleaks/betterleaks); we also added more custom rules for common database connect strings.

## Entire Web

### Navigation and UI Fixes

- Fixed page titles and commit SHA breadcrumbs so you always know where you are.
- Missing-page handling is smoother, with working command palette access on authenticated 404s, cleaner shared-link error states, and fewer incorrect 404s on repository default branches.
- Critical fonts are now preloaded, making pages render faster on first visit.

### Better Session and Checkpoint Display

- Session transcripts now refresh automatically when you open the share dialog, so you always see the latest content.
- Fixed empty messages appearing in Claude Code transcripts.

### Smoother Login and Authentication

- Login is easier and post-sign-in redirects back to the page you were trying to visit.

## Go-Git

### Features and Improvements

- Added Git-compatible `wildmatch` support for `.gitignore`, making ignored file behavior line up more closely with standard Git.
  - Thank you, [@AriehSchneier](https://github.com/AriehSchneier) for your contribution!
- Added submodule support for SHA-256 repositories, improving compatibility with newer Git repository formats.

### Bug Fixes

- Fixed multi-round `upload-pack` negotiation bugs, improving fetch and clone reliability in more complex transport flows.
  - Thank you, [@manland](https://github.com/manland) for your contribution!
- Deduplicated filesystem object iteration, reducing repeated work when walking stored Git objects.
- Closed packfile iterators after use, preventing leaked file handles during repository operations.
  - Thank you, [@aymanbagabas](https://github.com/aymanbagabas) for your contribution!
- Fixed local clone URL resolution so relative paths are resolved from the current working directory, matching developer expectations when cloning from local paths.
  - Thank you, [@AriehSchneier](https://github.com/AriehSchneier) for your contribution!

### Housekeeping

- Added a DCO sign-off requirement for contributions.
- Updated docs, examples, tests, and contributor guidance, including Go version support comments, deprecated `io/ioutil` usage, PR review instructions, and test stability fixes.
  - Thank you, [@alexandear](https://github.com/alexandear), [@aymanbagabas](https://github.com/aymanbagabas), and [@shawntoffel](https://github.com/shawntoffel) for your contributions!

That's the dispatch. As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) with questions, feature requests, or observations about the fundamental absurdity of software. Or file an [issue](https://github.com/entireio/cli/issues) if you've found a bug.

Until next week...if there even is one.

Boop.
